import axios from 'axios';
import {
Camera, Stream, Detection, StreamStatus,
User, Event, Analytics, SystemLog, Recording, SystemMetric, Notification,
LoginRequest, RegisterRequest, TokenResponse, AnalyticsSummary, SystemHealthResponse,
EventSeverity, EventType, UserRole, LogLevel, Role, Permission, LocationGroup
} from '../types';

import { API_BASE } from '../config';
const API_BASE_URL = `${API_BASE}/api/v1`;

export const api = axios.create({
baseURL: API_BASE_URL,
timeout: 150000,
});

// ================= AUTH INTERCEPTORS =================

api.interceptors.request.use((config) => {
const token = localStorage.getItem('astravision_token');
if (token) {
config.headers.Authorization = `Bearer ${token}`;
}
return config;
});

api.interceptors.response.use(
(response) => response,
(error) => {
if (error.response?.status === 401) {
console.log(`🔐 Token expired → redirecting to login`);
localStorage.removeItem('astravision_token');
localStorage.removeItem('astravision_user');
localStorage.removeItem('astravision_camera_cache');
window.location.href = '/login';
}
return Promise.reject(error);
}
);

// ================= AUTH FETCH =================
// Wrapper around the native fetch() that injects the Bearer token, mirroring
// what the axios interceptor does for api.* calls.
export function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('astravision_token');
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

// ================= CAMERA =================

export const cameraService = {
getCameras: async (params?: any): Promise<Camera[]> =>
(await api.get('/cameras', { params })).data,

// The snapshot endpoint is behind camera_router's blanket AUTH_REQUIRED (main.py), so a plain
// <img src> can't carry the Bearer token -- same reason as getImageBlobUrl below. Fetch via
// authFetch and hand back a blob object URL; callers must revokeObjectURL when done with it.
getSnapshotBlobUrl: async (cameraId: number, fresh = false, profile?: 'main' | 'sub'): Promise<string> => {
  const params = new URLSearchParams();
  if (fresh) params.append('fresh', 'true');
  if (profile) params.append('profile', profile);
  if (fresh || profile) params.append('t', String(Date.now()));
  const qs = params.toString();
  const url = `${API_BASE_URL}/cameras/${cameraId}/snapshot${qs ? `?${qs}` : ''}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Failed to load snapshot (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
},

getStreamHealth: async (cameraId: number, streamProfile: 'main' | 'sub' = 'main'): Promise<any> =>
  (await api.get(`/cameras/${cameraId}/stream-health`, { params: { stream_profile: streamProfile } })).data,

getStreamDiagnostics: async (cameraId: number): Promise<any> =>
  (await api.get(`/cameras/${cameraId}/stream-diagnostics`)).data,

setAllowDualStream: async (cameraId: number, allow: boolean): Promise<any> =>
  (await api.post(`/cameras/${cameraId}/allow-dual-stream`, null, { params: { allow } })).data,

getCamera: async (id: number): Promise<Camera> =>
(await api.get(`/cameras/${id}`)).data,

createCamera: async (camera: Partial<Camera>): Promise<Camera> =>
(await api.post('/cameras', camera)).data,

updateCamera: async (id: number, camera: Partial<Camera>): Promise<Camera> =>
(await api.put(`/cameras/${id}`, camera)).data,

deleteCamera: async (id: number): Promise<void> =>
await api.delete(`/cameras/${id}`),

discoverCameras: async () =>
(await api.post('/cameras/discover')).data,

manualAdd: async (request: any) =>
(await api.post('/cameras/manual-add', request)).data,

detectHardwareAI: async (id: number): Promise<{
  camera_id: number;
  is_ai_camera: boolean;
  capabilities: string[];
  detection_method: string;
}> => (await api.post(`/cameras/${id}/detect-hardware-ai`)).data,

probeOnvifServices: async (id: number): Promise<{
  services: { name: string; namespace: string; url: string }[];
  error: string | null;
  port_used: number | null;
}> => (await api.post(`/cameras/${id}/onvif-services`)).data,
};

// ================= STREAM =================

export const streamService = {
getStreams: async (params?: any): Promise<Stream[]> =>
(await api.get('/streams', { params })).data,

getStream: async (id: number): Promise<Stream> =>
(await api.get(`/streams/${id}`)).data,

createStream: async (stream: any): Promise<Stream> =>
(await api.post('/streams', stream)).data,

deleteStream: async (id: number): Promise<void> =>
await api.delete(`/streams/${id}`),

getStreamStatus: async (id: number): Promise<StreamStatus> =>
(await api.get(`/streams/${id}/status`)).data,

startStream: async (id: number): Promise<void> =>
await api.post(`/streams/${id}/start`),

stopStream: async (id: number): Promise<void> =>
await api.post(`/streams/${id}/stop`),
};

// ================= AUTH =================

export const authService = {
login: async (credentials: LoginRequest): Promise<TokenResponse> => {
const res = await api.post('/auth/login', credentials);
localStorage.setItem('astravision_token', res.data.access_token);
return res.data;
},

register: async (userData: RegisterRequest): Promise<User> =>
(await api.post('/auth/register', userData)).data,

logout: (): void => localStorage.removeItem('astravision_token'),

getCurrentUser: async (): Promise<User> =>
(await api.get('/users/me')).data,
};

// ================= USERS =================

export const userService = {
getUsers: async (params?: any): Promise<User[]> =>
(await api.get('/users', { params })).data,

getUser: async (id: number): Promise<User> =>
(await api.get(`/users/${id}`)).data,

createUser: async (userData: RegisterRequest): Promise<User> =>
(await api.post('/users', userData)).data,

updateUser: async (id: number, userData: Partial<User>): Promise<User> =>
(await api.put(`/users/${id}`, userData)).data,

deleteUser: async (id: number): Promise<void> =>
await api.delete(`/users/${id}`),
};

// ================= PROFILE =================

export const profileService = {
  getProfile: async (): Promise<User> =>
    (await api.get('/users/me')).data,

  updateProfile: async (data: { full_name?: string; email?: string; phone?: string; bio?: string }): Promise<User> =>
    (await api.put('/users/me/profile', data)).data,

  uploadAvatar: async (file: File): Promise<{ profile_picture: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    return (await api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data
  },

  changePassword: async (data: { current_password: string; new_password: string }): Promise<void> => {
    await api.put('/users/me/password', data)
  },
}

// ================= EVENTS =================

export const eventService = {
getEvents: async (params?: any): Promise<Event[]> =>
(await api.get('/events', { params })).data,

getRecentEvents: async (limit = 50): Promise<Event[]> =>
(await api.get('/events/recent', { params: { limit } })).data,

getCriticalEvents: async (params?: any): Promise<Event[]> =>
(await api.get('/events/critical', { params })).data,

createEvent: async (eventData: any): Promise<Event> =>
(await api.post('/events', eventData)).data,

acknowledgeEvent: async (eventId: number): Promise<void> =>
await api.put(`/events/${eventId}/acknowledge`),

deleteEvent: async (eventId: number): Promise<void> =>
await api.delete(`/events/${eventId}`),

getEventStats: async (): Promise<any> =>
(await api.get('/events/stats/summary')).data,
};

// ================= ANALYTICS =================

export const analyticsService = {
getAnalytics: async (params?: any): Promise<Analytics[]> =>
(await api.get('/analytics', { params })).data,

getAnalyticsSummary: async (days = 7): Promise<AnalyticsSummary> =>
(await api.get('/analytics/summary', { params: { days } })).data,
};

// ================= SYSTEM =================

export const systemService = {
getHealth: async (): Promise<SystemHealthResponse> =>
(await api.get('/health')).data,

getMetrics: async (params?: any): Promise<SystemMetric[]> =>
(await api.get('/metrics', { params })).data,

getLatestMetrics: async (): Promise<Record<string, any>> =>
(await api.get('/metrics/latest')).data,

getCameraStatus: async (): Promise<any> =>
(await api.get('/cameras/status')).data,

getSystemInfo: async (): Promise<any> =>
(await api.get('/system/info')).data,

getFullTelemetry: async (): Promise<any> =>
(await api.get('/system/full-telemetry')).data,

getStorageBreakdown: async (): Promise<any> =>
(await api.get('/system/storage/breakdown')).data,

purgeStorageDate: async (date: string, preserve_archived: boolean = true): Promise<any> =>
(await api.post('/system/storage/purge-date', { date, preserve_archived })).data,

cleanStorageCache: async (): Promise<any> =>
(await api.post('/system/storage/clean-cache')).data,

restartWorker: async (cameraId: number, moduleKey: string): Promise<any> =>
(await api.post(`/system/cameras/${cameraId}/${moduleKey}/restart`)).data,

bulkWorkerAction: async (action: 'start_all' | 'stop_all' | 'restart_all', cameraId?: number): Promise<any> =>
(await api.post('/system/bulk-workers', { action, camera_id: cameraId })).data,
};

// ================= RECORDINGS =================

export const recordingService = {
getRecordings: async (params?: any): Promise<Recording[]> =>
(await api.get('/recordings/', { params })).data,

getRecording: async (id: number): Promise<Recording> =>
(await api.get(`/recordings/${id}`)).data,

getStreamUrl: (id: number): string =>
`${API_BASE}/api/v1/recordings/${id}/stream`,

getDownloadUrl: (id: number): string =>
`${API_BASE}/api/v1/recordings/${id}/download`,

deleteRecording: async (id: number): Promise<void> =>
await api.delete(`/recordings/${id}`),

archiveRecording: async (id: number): Promise<void> =>
await api.put(`/recordings/${id}/archive`),

getManualStatus: async (): Promise<Record<number, any>> =>
(await api.get('/recordings/manual/status')).data,

getCameraManualStatus: async (cameraId: number): Promise<any> =>
(await api.get(`/recordings/manual/status/${cameraId}`)).data,

startManualRecording: async (cameraId: number): Promise<any> =>
(await api.post(`/recordings/manual/start/${cameraId}`)).data,

stopManualRecording: async (cameraId: number): Promise<any> =>
(await api.post(`/recordings/manual/stop/${cameraId}`)).data,

updateConfig: async (cameraId: number, config: { enable_recording: boolean; recording_mode?: string; retention_days?: number }): Promise<any> =>
(await api.post(`/recordings/config/${cameraId}`, config)).data,

batchUpdateConfig: async (batch: { camera_ids: number[]; enable_recording: boolean; recording_mode?: string; retention_days?: number }): Promise<any> =>
(await api.post('/recordings/batch-config', batch)).data,

getPipelineTelemetry: async (): Promise<any[]> =>
(await api.get('/recordings/pipeline-telemetry')).data,

getStorageStats: async (): Promise<any> =>
(await api.get('/recordings/storage/stats')).data,

updateStoragePolicy: async (policy: {
  compression_mode?: string;
  continuous_loop?: boolean;
  auto_purge_threshold_percent?: number;
  retention_days?: number;
  protect_archived?: boolean;
}): Promise<any> =>
(await api.post('/recordings/storage/policy', policy)).data,

purgeStorageRecordings: async (payload: {
  older_than_days?: number;
  target_free_gb?: number;
  preserve_archived?: boolean;
}): Promise<any> =>
(await api.post('/recordings/storage/purge', payload)).data,

calculateStorageRetention: async (payload: {
  cameras_count: number;
  compression_mode?: string;
  allocated_gb?: number;
  custom_bitrate_mbps?: number;
}): Promise<any> =>
(await api.post('/recordings/storage/calculate', payload)).data,
};

// ================= DETECTIONS =================

export const detectionService = {
getDetections: async (params?: any): Promise<any[]> =>
(await api.get('/detections', { params })).data,

getDetection: async (id: number): Promise<any> =>
(await api.get(`/detections/${id}`)).data,

getDetectionStats: async (): Promise<any> =>
(await api.get('/detections/stats/summary')).data,

deleteDetection: async (id: number): Promise<void> =>
await api.delete(`/detections/${id}`),
};

// ================= ROLES =================

export const roleService = {
getRoles: async (params?: any): Promise<Role[]> =>
(await api.get('/roles', { params })).data,

getRole: async (id: number): Promise<Role> =>
(await api.get(`/roles/${id}`)).data,

createRole: async (roleData: any): Promise<Role> =>
(await api.post('/roles', roleData)).data,

updateRole: async (id: number, roleData: any): Promise<Role> =>
(await api.put(`/roles/${id}`, roleData)).data,

deleteRole: async (id: number): Promise<void> =>
await api.delete(`/roles/${id}`),

updateRolePermissions: async (id: number, permissions: string[]): Promise<void> =>
await api.put(`/roles/${id}/permissions`, permissions),
};

// ================= LOCATION GROUP =================

export const locationGroupService = {
getLocationGroups: async (): Promise<LocationGroup[]> =>
(await api.get('/location_groups')).data,

createLocationGroup: async (data: any): Promise<LocationGroup> =>
(await api.post('/location_groups', data)).data,

updateLocationGroup: async (id: number, data: any): Promise<LocationGroup> =>
(await api.put(`/location_groups/${id}`, data)).data,

deleteLocationGroup: async (id: number): Promise<void> =>
await api.delete(`/location_groups/${id}`),
};

// ================= WEBRTC =================



// ================= WEBRTC =================

export interface WebRTCOfferRequest {
  sdp: string
  type: RTCSdpType
  rtsp_url: string
}

export interface WebRTCAnswerResponse {
  sdp: string
  type: RTCSdpType   // ✅ FIXED HERE
}

export const webrtcService = {
  async createOffer(
    payload: WebRTCOfferRequest
  ): Promise<WebRTCAnswerResponse> {

    const res = await fetch("/api/v1/webrtc/offer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })

    if (!res.ok) {
      throw new Error("WebRTC offer failed")
    }

    return res.json()
  }
}
// ================= STREAM URL HELPER =================

export const getBestStreamUrl = (camera?: Camera, stream?: Stream): string | undefined => {
if (stream?.hls_url) return stream.hls_url;
if (camera?.hls_url) return camera.hls_url;
if (stream?.webrtc_url) return stream.webrtc_url;
if (camera?.rtsp_url) return camera.rtsp_url;
return undefined;
};

export const detectStreamType = (url?: string) => {
if (!url) return "none";
if (url.includes(".m3u8")) return "hls";
if (url.includes("webrtc")) return "webrtc";
if (url.startsWith("rtsp")) return "rtsp";
return "unknown";
};



export const aiService = {
  toggle: async (enabled: boolean) => {
    const res = await fetch("/api/v1/ai/toggle", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled })
    })
    return res.json()
  },
  getStatus: async (): Promise<{ ai_enabled: boolean }> => {
    const res = await fetch("/api/v1/ai/status")
    return res.json()
  }
}

// ================= FACE DETECTIONS =================

export const faceDetectionService = {
  getFaceDetections: async (params?: any): Promise<any[]> =>
    (await api.get('/face-detections', { params })).data,

  getFaceDetection: async (id: number): Promise<any> =>
    (await api.get(`/face-detections/${id}`)).data,

  createFaceDetection: async (data: any): Promise<any> =>
    (await api.post('/face-detections', data)).data,

  updateFaceDetection: async (id: number, data: any): Promise<any> =>
    (await api.put(`/face-detections/${id}`, data)).data,

  deleteFaceDetection: async (id: number): Promise<void> =>
    await api.delete(`/face-detections/${id}`),
};

// ================= VEHICLE DETECTIONS =================

export const vehicleDetectionService = {
  getVehicleDetections: async (params?: any): Promise<any[]> =>
    (await api.get('/vehicle-detections', { params })).data,

  getVehicleDetection: async (id: number): Promise<any> =>
    (await api.get(`/vehicle-detections/${id}`)).data,

  createVehicleDetection: async (data: any): Promise<any> =>
    (await api.post('/vehicle-detections', data)).data,

  updateVehicleDetection: async (id: number, data: any): Promise<any> =>
    (await api.put(`/vehicle-detections/${id}`, data)).data,

  deleteVehicleDetection: async (id: number): Promise<void> =>
    await api.delete(`/vehicle-detections/${id}`),
};

// ================= LICENSE PLATES =================

export const licensePlateService = {
  getLicensePlates: async (params?: any): Promise<any[]> =>
    (await api.get('/detected-plates', { params })).data,

  getLicensePlate: async (id: number): Promise<any> =>
    (await api.get(`/detected-plates/${id}`)).data,

  createLicensePlate: async (data: any): Promise<any> =>
    (await api.post('/detected-plates', data)).data,

  updateLicensePlate: async (id: number, data: any): Promise<any> =>
    (await api.put(`/detected-plates/${id}`, data)).data,

  deleteLicensePlate: async (id: number): Promise<any> =>
    (await api.delete(`/detected-plates/${id}`)).data,

  blockLicensePlate: async (id: number): Promise<any> =>
    (await api.put(`/detected-plates/${id}/block`)).data,

  unblockLicensePlate: async (id: number): Promise<any> =>
    (await api.put(`/detected-plates/${id}/unblock`)).data,

  getLicensePlatesStats: async (): Promise<any> =>
    (await api.get('/detected-plates/stats/summary')).data,
}

// ================= LOGS =================

export const logService = {
  getLogs: async (params?: any): Promise<any[]> =>
    (await api.get('/logs', { params })).data,

  getLog: async (id: number): Promise<any> =>
    (await api.get(`/logs/${id}`)).data,

  createLog: async (data: any): Promise<any> =>
    (await api.post('/logs', data)).data,

  getLogStats: async (days?: number): Promise<any> =>
    (await api.get('/logs/stats', { params: { days: days || 7 } })).data,
};

// ================= BLACKLIST =================

export const blacklistService = {
  getBlacklist: async (params?: any): Promise<any[]> =>
    (await api.get('/blacklist', { params })).data,

  getBlacklistItem: async (id: number): Promise<any> =>
    (await api.get(`/blacklist/${id}`)).data,

  createBlacklistItem: async (data: any): Promise<any> =>
    (await api.post('/blacklist', data)).data,

  updateBlacklistItem: async (id: number, data: any): Promise<any> =>
    (await api.put(`/blacklist/${id}`, data)).data,

  deleteBlacklistItem: async (id: number): Promise<void> =>
    await api.delete(`/blacklist/${id}`),
};

// ================= BLACKLIST ALERTS =================

export const blacklistAlertService = {
  getBlacklistAlerts: async (params?: any): Promise<any[]> =>
    (await api.get('/blacklist-alerts', { params })).data,

  getBlacklistAlert: async (id: number): Promise<any> =>
    (await api.get(`/blacklist-alerts/${id}`)).data,

  createBlacklistAlert: async (data: any): Promise<any> =>
    (await api.post('/blacklist-alerts', data)).data,

  updateBlacklistAlert: async (id: number, data: any): Promise<any> =>
    (await api.put(`/blacklist-alerts/${id}`, data)).data,

  deleteBlacklistAlert: async (id: number): Promise<void> =>
    await api.delete(`/blacklist-alerts/${id}`),

  getBlacklistAlertsCount: async (): Promise<number> =>
    (await api.get('/blacklist-alerts/')).data,
};

// ================= COUNT SERVICES =================

export const countService = {
  // Total counts
  getFaceDetectionsCount: async (): Promise<number> =>
    (await api.get('/face-detections/count')).data.count,

  getVehicleDetectionsCount: async (): Promise<number> =>
    (await api.get('/vehicle-detections/count')).data.count,

  getLicensePlatesCount: async (): Promise<number> =>
    (await api.get('/license-plates/count')).data.count,

  getBlacklistCount: async (): Promise<number> =>
    (await api.get('/blacklist/count')).data.count,

  getBlacklistAlertsCount: async (): Promise<number> =>
    (await api.get('/blacklist-alerts/count')).data.count,

  getDetectionsCountToday: async (): Promise<number> =>
    (await api.get('/detections/count/today')).data.count,

  // Daily counts
  getFaceDetectionsCountToday: async (): Promise<number> =>
    (await api.get('/face-detections/count/today')).data.count,

  getVehicleDetectionsCountToday: async (): Promise<number> =>
    (await api.get('/vehicle-detections/count/today')).data.count,

  getLicensePlatesCountToday: async (): Promise<number> =>
    (await api.get('/license-plates/count/today')).data.count,

  getBlacklistCountToday: async (): Promise<number> =>
    (await api.get('/blacklist/count/today')).data.count,

  getBlacklistAlertsCountToday: async (): Promise<number> =>
    (await api.get('/blacklist-alerts/count/today')).data.count,

  getSnapshotsCountToday: async (): Promise<number> =>
    (await api.get('/snapshots/count/today')).data.count,
};

// ================= SNAPSHOT SERVICES =================

export const snapshotService = {
  getSnapshots: async (params?: any): Promise<any[]> =>
    (await api.get('/snapshots', { params })).data,

  getSnapshot: async (id: number): Promise<any> =>
    (await api.get(`/snapshots/${id}`)).data,

  startSnapshots: async (): Promise<any> =>
    (await api.post('/snapshots/start')).data,

  stopSnapshots: async (): Promise<any> =>
    (await api.post('/snapshots/stop')).data,
};

// ================= KNOWN FACES =================

export const knownFacesService = {
  getFaces: async (params?: any): Promise<any[]> =>
    (await api.get('/faces', { params })).data,

  getFace: async (id: number): Promise<any> =>
    (await api.get(`/faces/${id}`)).data,

  registerFace: async (name: string): Promise<any> =>
    (await api.post('/faces/register', { name })).data,

  updateFace: async (id: number, data: any): Promise<any> =>
    (await api.put(`/faces/${id}`, data)).data,

  deleteFace: async (id: number): Promise<void> =>
    (await api.delete(`/faces/${id}`)).data,
};

// ================= BLOCKED PERSONS =================

export const blockedPersonsService = {
  getBlockedPersons: async (params?: any): Promise<any[]> =>
    (await api.get('/blocked-persons', { params })).data,

  getBlockedPerson: async (id: number): Promise<any> =>
    (await api.get(`/blocked-persons/${id}`)).data,

  createBlockedPerson: async (data: any): Promise<any> =>
    (await api.post('/blocked-persons', data)).data,

  updateBlockedPerson: async (id: number, data: any): Promise<any> =>
    (await api.put(`/blocked-persons/${id}`, data)).data,

  deleteBlockedPerson: async (id: number): Promise<void> =>
    (await api.delete(`/blocked-persons/${id}`)).data,

  uploadPhoto: async (id: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return (await api.post(`/blocked-persons/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })).data;
  },

  batchUpload: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return (await api.post('/blocked-persons/batch-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })).data;
  },

  getStatistics: async (days = 7): Promise<any> =>
    (await api.get('/blacklist/statistics', { params: { days } })).data,

  testDetection: async (cameraId = 1): Promise<any> =>
    (await api.post('/blacklist/test', { camera_id: cameraId })).data,
};

// ================= BLACKLIST ALERTS =================

export const blacklistAlertsService = {
  getAlerts: async (params?: any): Promise<any[]> =>
    (await api.get('/blacklist-alerts', { params })).data,

  getAlert: async (id: number): Promise<any> =>
    (await api.get(`/blacklist-alerts/${id}`)).data,

  createAlert: async (data: any): Promise<any> =>
    (await api.post('/blacklist-alerts', data)).data,

  updateAlert: async (id: number, data: any): Promise<any> =>
    (await api.put(`/blacklist-alerts/${id}`, data)).data,

  deleteAlert: async (id: number): Promise<void> =>
    (await api.delete(`/blacklist-alerts/${id}`)).data,

  getCount: async (): Promise<number> =>
    (await api.get('/blacklist-alerts/count')).data.count,

  getCountToday: async (): Promise<number> =>
    (await api.get('/blacklist-alerts/count/today')).data.count,
};

// ================= LICENSE =================

export const licenseService = {
  getInfo: async (): Promise<any> =>
    (await api.get('/license/info')).data,

  activate: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return (await api.post('/license/activate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  validate: async (): Promise<any> =>
    (await api.get('/license/validate')).data,

  enforceCameraLimit: async (): Promise<any> =>
    (await api.post('/license/enforce-cameras')).data,
};

// ================= AI LICENSING (entitlements) =================
// Thin wrappers over the EXISTING backend endpoints. The backend verifies signatures, installation binding and
// validity; the browser never decides whether a licence is genuine and never sees keys or certificate contents.

export const aiLicenseService = {
  list: async (): Promise<any> => (await api.get('/ai/entitlements')).data,

  get: async (entitlementId: string): Promise<any> =>
    (await api.get(`/ai/entitlements/${encodeURIComponent(entitlementId)}`)).data,

  status: async (): Promise<any> => (await api.get('/ai/entitlements/status')).data,

  audit: async (limit = 200): Promise<any[]> => (await api.get('/ai/entitlements/audit', { params: { limit } })).data,

  // The certificate is sent as parsed JSON; the backend re-verifies everything.
  importCertificate: async (certificate: unknown, onProgress?: (percent: number) => void): Promise<any> =>
    (await api.post('/ai/entitlements/import', { certificate }, {
      onUploadProgress: (e: any) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)) },
    })).data,

  sync: async (): Promise<any> => (await api.post('/ai/entitlements/sync')).data,

  cameraRefs: async (): Promise<any[]> => (await api.get('/ai/camera-refs')).data,

  setCameraRef: async (cameraId: number, cameraRef: string): Promise<any> =>
    (await api.put(`/ai/cameras/${cameraId}/ref`, { camera_ref: cameraRef })).data,

  // Unlink a camera's mapped reference so its licensed slot can be moved to another camera.
  clearCameraRef: async (cameraId: number): Promise<any> => (await api.delete(`/ai/cameras/${cameraId}/ref`)).data,

  cameraEntitlements: async (cameraId: number): Promise<any> =>
    (await api.get(`/ai/cameras/${cameraId}/entitlements`)).data,
};

// ================= AI MODULES =================

export const aiModuleService = {
  listCatalog: async (): Promise<any[]> =>
    (await api.get('/ai-modules')).data,

  // What the module's DEPLOYED model detects (class-presence rules may only target these classes).
  getModuleModel: async (moduleKey: string): Promise<any> =>
    (await api.get(`/ai-modules/models/${moduleKey}`)).data,

  listCameraModules: async (cameraId: number): Promise<any[]> =>
    (await api.get(`/ai-modules/cameras/${cameraId}`)).data,

  listAllCameraModules: async (): Promise<any[]> =>
    (await api.get('/ai-modules/all-camera-modules')).data,

  assignModule: async (cameraId: number, moduleKey: string, body: any = {}): Promise<any> =>
    (await api.post(`/ai-modules/cameras/${cameraId}/${moduleKey}/assign`, body)).data,

  updateSettings: async (cameraId: number, moduleKey: string, body: any): Promise<any> =>
    (await api.put(`/ai-modules/cameras/${cameraId}/${moduleKey}/settings`, body)).data,

  enableModule: async (cameraId: number, moduleKey: string): Promise<any> =>
    (await api.post(`/ai-modules/cameras/${cameraId}/${moduleKey}/enable`)).data,

  disableModule: async (cameraId: number, moduleKey: string): Promise<any> =>
    (await api.post(`/ai-modules/cameras/${cameraId}/${moduleKey}/disable`)).data,

  unassignModule: async (cameraId: number, moduleKey: string): Promise<any> =>
    (await api.delete(`/ai-modules/cameras/${cameraId}/${moduleKey}`)).data,

  distributeServices: async (distributions: { module_key: string; from_camera_id: number; to_camera_id: number }[]): Promise<any> =>
    (await api.post('/ai-modules/distribute-services', { distributions })).data,

  listEvents: async (params?: { camera_id?: number; state?: string; limit?: number }): Promise<any[]> =>
    (await api.get('/ai-modules/events', { params })).data,

  // Cross-camera browser (AiEventsPage): adds group/rule_type/date-range/offset filtering
  // and hands back the total match count from X-Total-Count for pagination.
  listEventsPage: async (params?: {
    camera_id?: number; state?: string; module_key?: string; rule_type?: string; group?: string;
    query?: string; start_date?: string; end_date?: string; limit?: number; offset?: number;
  }): Promise<{ items: any[]; total: number }> => {
    const res = await api.get('/ai-modules/events', { params });
    const total = Number(res.headers['x-total-count'] ?? res.data.length);
    return { items: res.data, total };
  },

  acknowledgeEvent: async (eventId: number): Promise<any> =>
    (await api.post(`/ai-modules/events/${eventId}/acknowledge`)).data,

  deleteEvent: async (eventId: number): Promise<any> =>
    (await api.delete(`/ai-modules/events/${eventId}`)).data,

  bulkDeleteEvents: async (payload: {
    event_ids?: number[];
    all_matching?: boolean;
    camera_id?: number;
    group?: string;
    module_key?: string;
    rule_type?: string;
    state?: string;
    query?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ status: string; deleted_count: number }> =>
    (await api.post('/ai-modules/events/bulk-delete', payload)).data,

  // Evidence files are served from an auth-gated endpoint (not a public
  // static mount, since footage may be proprietary) -- fetch via authFetch
  // and hand back a blob object URL, same pattern as dataset images'
  // getImageBlobUrl. Callers must revokeObjectURL when done.
  getEvidenceBlobUrl: async (eventId: number, evidenceId: number): Promise<string> => {
    const res = await authFetch(`${API_BASE_URL}/ai-modules/events/${eventId}/evidence/${evidenceId}/file`);
    if (!res.ok) throw new Error(`Failed to load evidence (${res.status})`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  getEventDetail: async (eventId: number): Promise<any> =>
    (await api.get(`/ai-modules/events/${eventId}`)).data,

  extractEventCrops: async (eventId: number): Promise<any> =>
    (await api.post(`/ai-modules/events/${eventId}/extract-crops`)).data,

  extractAllCrops: async (params?: number | { limit?: number }): Promise<any> => {
    const limit = typeof params === 'number' ? params : (params?.limit ?? 50);
    return (await api.post('/ai-modules/events/extract-all-crops', null, { params: { limit } })).data;
  },

  searchByCrop: async (payload: {
    evidence_id: number;
    min_similarity?: number;
    limit?: number;
    camera_id?: number;
  }): Promise<{
    query: any;
    total_matches: number;
    matches: Array<{
      event_id: number;
      evidence_id: number;
      camera_id: number;
      camera_name: string;
      timestamp: string | null;
      rule_type?: string | null;
      similarity: number;
      state: string;
      confidence?: number;
      label?: string;
      matched_person_name?: string | null;
    }>;
  }> =>
    (await api.post('/ai-modules/events/search-by-crop', payload)).data,

  listWorkerStatus: async (cameraId?: number): Promise<any[]> =>
    (await api.get('/ai-modules/worker-status', { params: cameraId ? { camera_id: cameraId } : {} })).data,

  listZones: async (cameraId: number): Promise<any[]> =>
    (await api.get('/ai-modules/zones', { params: { camera_id: cameraId } })).data,

  createZone: async (body: any): Promise<any> =>
    (await api.post('/ai-modules/zones', body)).data,

  updateZone: async (zoneId: number, body: any): Promise<any> =>
    (await api.put(`/ai-modules/zones/${zoneId}`, body)).data,

  deleteZone: async (zoneId: number): Promise<any> =>
    (await api.delete(`/ai-modules/zones/${zoneId}`)).data,

  listLines: async (cameraId: number): Promise<any[]> =>
    (await api.get('/ai-modules/lines', { params: { camera_id: cameraId } })).data,

  createLine: async (body: any): Promise<any> =>
    (await api.post('/ai-modules/lines', body)).data,

  updateLine: async (lineId: number, body: any): Promise<any> =>
    (await api.put(`/ai-modules/lines/${lineId}`, body)).data,

  deleteLine: async (lineId: number): Promise<any> =>
    (await api.delete(`/ai-modules/lines/${lineId}`)).data,

  getLiveCount: async (cameraId: number, moduleKey: string): Promise<any> =>
    (await api.get('/ai-modules/live-count', { params: { camera_id: cameraId, module_key: moduleKey } })).data,

  getCounts: async (cameraId: number, moduleKey: string, since?: string, until?: string, bucket?: string): Promise<any> =>
    (await api.get('/ai-modules/counts', { params: { camera_id: cameraId, module_key: moduleKey, ...(since ? { since } : {}), ...(until ? { until } : {}), ...(bucket ? { bucket } : {}) } })).data,

  getDwellStats: async (cameraId: number, moduleKey: string, since?: string): Promise<any> =>
    (await api.get('/ai-modules/dwell-stats', { params: { camera_id: cameraId, module_key: moduleKey, ...(since ? { since } : {}) } })).data,

  getHeatmap: async (cameraId: number, moduleKey: string): Promise<any> =>
    (await api.get('/ai-modules/heatmap', { params: { camera_id: cameraId, module_key: moduleKey } })).data,

  listRules: async (cameraId: number): Promise<any[]> =>
    (await api.get('/ai-modules/rules', { params: { camera_id: cameraId } })).data,

  createRule: async (body: any): Promise<any> =>
    (await api.post('/ai-modules/rules', body)).data,

  updateRule: async (ruleId: number, body: any): Promise<any> =>
    (await api.put(`/ai-modules/rules/${ruleId}`, body)).data,

  deleteRule: async (ruleId: number): Promise<any> =>
    (await api.delete(`/ai-modules/rules/${ruleId}`)).data,

  // Vehicle Watchlist store: one global list of plates checked by every camera running a watchlist_match rule.
  listWatchlist: async (): Promise<any[]> =>
    (await api.get('/ai-modules/watchlist')).data,

  createWatchlistEntry: async (body: { plate: string; label?: string | null; reason?: string | null; active?: boolean }): Promise<any> =>
    (await api.post('/ai-modules/watchlist', body)).data,

  updateWatchlistEntry: async (entryId: number, body: { plate: string; label?: string | null; reason?: string | null; active?: boolean }): Promise<any> =>
    (await api.put(`/ai-modules/watchlist/${entryId}`, body)).data,

  deleteWatchlistEntry: async (entryId: number): Promise<any> =>
    (await api.delete(`/ai-modules/watchlist/${entryId}`)).data,

  importWatchlist: async (file: File): Promise<{ created: number; updated: number; skipped: number; errors: string[] }> => {
    const form = new FormData()
    form.append('file', file)
    return (await api.post('/ai-modules/watchlist/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
};

// ================= DATASET STUDIO (Custom Model Studio) =================

export const datasetStudioService = {
  // What this installation allows (e.g. can_import=false on customer installs: datasets/models can be edited but not imported).
  getCapabilities: async (): Promise<{ can_import: boolean }> => (await api.get('/datasets/capabilities')).data,

  listDatasets: async (): Promise<any[]> => (await api.get('/datasets')).data,

  createDataset: async (body: any): Promise<any> => (await api.post('/datasets', body)).data,

  getDataset: async (datasetId: number): Promise<any> => (await api.get(`/datasets/${datasetId}`)).data,

  deleteDataset: async (datasetId: number): Promise<any> => (await api.delete(`/datasets/${datasetId}`)).data,

  uploadZip: async (datasetId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return (await api.post(`/datasets/${datasetId}/upload-zip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  uploadImages: async (datasetId: number, files: File[]): Promise<any> => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return (await api.post(`/datasets/${datasetId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  uploadVideo: async (datasetId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return (await api.post(`/datasets/${datasetId}/upload-video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  extractFrames: async (datasetId: number, body: { video_token: string; fps: number; max_frames: number }): Promise<any> =>
    (await api.post(`/datasets/${datasetId}/extract-frames`, body)).data,

  listImages: async (
    datasetId: number,
    params: { limit?: number; offset?: number; split?: string; search?: string; has_annotations?: boolean; sort?: string } = {},
  ): Promise<{ total: number; items: any[] }> =>
    (await api.get(`/datasets/${datasetId}/images`, { params })).data,

  getImage: async (datasetId: number, imageId: number): Promise<any> =>
    (await api.get(`/datasets/${datasetId}/images/${imageId}`)).data,

  // Dataset images are served from an auth-gated endpoint (not a public
  // static mount, since they may be proprietary customer data), so a plain
  // <img src> can't carry the Bearer token — fetch via authFetch and hand
  // back a blob object URL instead. Callers must revokeObjectURL when done.
  getImageBlobUrl: async (datasetId: number, imageId: number): Promise<string> => {
    const res = await authFetch(`${API_BASE_URL}/datasets/${datasetId}/images/${imageId}/file`);
    if (!res.ok) throw new Error(`Failed to load image (${res.status})`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  deleteImage: async (datasetId: number, imageId: number): Promise<any> =>
    (await api.delete(`/datasets/${datasetId}/images/${imageId}`)).data,

  updateImageSplit: async (datasetId: number, imageId: number, split: string): Promise<any> =>
    (await api.put(`/datasets/${datasetId}/images/${imageId}/split`, { split })).data,

  listClasses: async (datasetId: number): Promise<any[]> => (await api.get(`/datasets/${datasetId}/classes`)).data,

  createClass: async (datasetId: number, name: string): Promise<any> =>
    (await api.post(`/datasets/${datasetId}/classes`, { name })).data,

  renameClass: async (datasetId: number, classId: number, name: string): Promise<any> =>
    (await api.put(`/datasets/${datasetId}/classes/${classId}`, { name })).data,

  deleteClass: async (datasetId: number, classId: number): Promise<any> =>
    (await api.delete(`/datasets/${datasetId}/classes/${classId}`)).data,

  listAnnotations: async (datasetId: number, imageId: number): Promise<any[]> =>
    (await api.get(`/datasets/${datasetId}/images/${imageId}/annotations`)).data,

  createAnnotation: async (datasetId: number, imageId: number, body: any): Promise<any> =>
    (await api.post(`/datasets/${datasetId}/images/${imageId}/annotations`, body)).data,

  updateAnnotation: async (datasetId: number, imageId: number, annotationId: number, body: any): Promise<any> =>
    (await api.put(`/datasets/${datasetId}/images/${imageId}/annotations/${annotationId}`, body)).data,

  deleteAnnotation: async (datasetId: number, imageId: number, annotationId: number): Promise<any> =>
    (await api.delete(`/datasets/${datasetId}/images/${imageId}/annotations/${annotationId}`)).data,

  validateDataset: async (datasetId: number): Promise<any> =>
    (await api.post(`/datasets/${datasetId}/validate`)).data,

  createVersion: async (datasetId: number): Promise<any> =>
    (await api.post(`/datasets/${datasetId}/versions`)).data,

  listVersions: async (datasetId: number): Promise<any[]> => (await api.get(`/datasets/${datasetId}/versions`)).data,

  getVersion: async (datasetId: number, versionId: number): Promise<any> =>
    (await api.get(`/datasets/${datasetId}/versions/${versionId}`)).data,

  exportVersionUrl: (datasetId: number, versionId: number): string =>
    `${API_BASE_URL}/datasets/${datasetId}/versions/${versionId}/export`,

  // Typed-in license / attribution / source reference (nothing is fetched or verified anywhere).
  updateProvenance: async (datasetId: number, body: any): Promise<any> =>
    (await api.put(`/datasets/${datasetId}/provenance`, body)).data,

  acknowledgeLicense: async (datasetId: number): Promise<any> =>
    (await api.post(`/datasets/${datasetId}/acknowledge-license`)).data,

  // Training jobs
  listTrainingJobs: async (): Promise<any[]> => (await api.get('/datasets/training-jobs')).data,

  startTraining: async (body: any): Promise<any> => (await api.post('/datasets/training-jobs', body)).data,

  getTrainingJob: async (jobId: number): Promise<any> => (await api.get(`/datasets/training-jobs/${jobId}`)).data,

  cancelTraining: async (jobId: number): Promise<any> =>
    (await api.post(`/datasets/training-jobs/${jobId}/cancel`)).data,

  getTrainingLogs: async (jobId: number, tailLines = 200): Promise<any> =>
    (await api.get(`/datasets/training-jobs/${jobId}/logs`, { params: { tail_lines: tailLines } })).data,

  registerCandidate: async (jobId: number): Promise<any> =>
    (await api.post(`/datasets/training-jobs/${jobId}/register`)).data,
};

// ================= MODEL ARTIFACT LIFECYCLE =================
// trained -> approved -> active -> archived (see model_training_service.py).
// Every transition here is an explicit admin action — nothing auto-deploys.

export const modelArtifactService = {
  list: async (moduleKey?: string, status?: string): Promise<any[]> =>
    (await api.get('/datasets/model-artifacts', { params: { module_key: moduleKey, status } })).data,

  get: async (artifactId: number): Promise<any> =>
    (await api.get(`/datasets/model-artifacts/${artifactId}`)).data,

  approve: async (artifactId: number): Promise<any> =>
    (await api.post(`/datasets/model-artifacts/${artifactId}/approve`)).data,

  reject: async (artifactId: number): Promise<any> =>
    (await api.post(`/datasets/model-artifacts/${artifactId}/reject`)).data,

  deploy: async (artifactId: number, comment?: string, acknowledgeClassWarning = false): Promise<any> =>
    (await api.post(`/datasets/model-artifacts/${artifactId}/deploy`, { comment, acknowledge_class_warning: acknowledgeClassWarning })).data,

  // Model classes vs the module (and the dataset it was trained on). See model_import.class_compatibility.
  compatibility: async (artifactId: number, moduleKey?: string): Promise<any> =>
    (await api.get(`/datasets/model-artifacts/${artifactId}/compatibility`, { params: { module_key: moduleKey } })).data,

  // Manual community model import: a .pt the person downloaded themselves. Becomes a CANDIDATE; never auto-deployed.
  importModel: async (file: File, fields: Record<string, string | boolean | undefined>): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== '') formData.append(k, String(v)); });
    return (await api.post('/datasets/model-artifacts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },

  rollback: async (artifactId: number, comment?: string): Promise<any> =>
    (await api.post(`/datasets/model-artifacts/${artifactId}/rollback`, { comment })).data,

  listAudit: async (moduleKey?: string): Promise<any[]> =>
    (await api.get('/datasets/model-artifacts/audit', { params: { module_key: moduleKey } })).data,
};

// ================= IP SPEAKERS & ANNOUNCEMENTS =================

export const speakerService = {
  getSpeakers: async (params?: any): Promise<any[]> =>
    (await api.get('/speakers', { params })).data,

  getSpeaker: async (id: number): Promise<any> =>
    (await api.get(`/speakers/${id}`)).data,

  createSpeaker: async (data: any): Promise<any> =>
    (await api.post('/speakers', data)).data,

  updateSpeaker: async (id: number, data: any): Promise<any> =>
    (await api.put(`/speakers/${id}`, data)).data,

  deleteSpeaker: async (id: number): Promise<any> =>
    (await api.delete(`/speakers/${id}`)).data,

  testSpeaker: async (id: number): Promise<any> =>
    (await api.post(`/speakers/${id}/test`)).data,

  playSpeaker: async (id: number, message?: string): Promise<any> =>
    (await api.post(`/speakers/${id}/play`, null, { params: { message } })).data,

  toggleMute: async (id: number): Promise<any> =>
    (await api.post(`/speakers/${id}/mute`)).data,

  setVolume: async (id: number, volume: number): Promise<any> =>
    (await api.post(`/speakers/${id}/volume`, null, { params: { volume } })).data,

  testAllSpeakers: async (): Promise<any> =>
    (await api.post('/speakers/test-all')).data,

  getCompatibilityMatrix: async (): Promise<any> =>
    (await api.get('/speakers/compatibility-matrix')).data,

  getSpeakerGroups: async (): Promise<any[]> =>
    (await api.get('/speaker-groups')).data,

  createSpeakerGroup: async (data: any): Promise<any> =>
    (await api.post('/speaker-groups', data)).data,

  updateSpeakerGroup: async (id: number, data: any): Promise<any> =>
    (await api.put(`/speaker-groups/${id}`, data)).data,

  deleteSpeakerGroup: async (id: number): Promise<any> =>
    (await api.delete(`/speaker-groups/${id}`)).data,

  getPricing: async (): Promise<any> =>
    (await api.get('/speakers/pricing')).data,
};

export const announcementService = {
  getPolicies: async (params?: any): Promise<any[]> =>
    (await api.get('/announcements/policies', { params })).data,

  createPolicy: async (data: any): Promise<any> =>
    (await api.post('/announcements/policies', data)).data,

  updatePolicy: async (id: number, data: any): Promise<any> =>
    (await api.put(`/announcements/policies/${id}`, data)).data,

  deletePolicy: async (id: number): Promise<any> =>
    (await api.delete(`/announcements/policies/${id}`)).data,

  triggerPolicyTest: async (id: number): Promise<any> =>
    (await api.post(`/announcements/policies/${id}/trigger`)).data,

  getAudioLibrary: async (category?: string): Promise<any[]> =>
    (await api.get('/announcements/audio', { params: { category } })).data,

  uploadAudio: async (file: File, name: string, category = 'General', language = 'en', description = ''): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('category', category);
    formData.append('language', language);
    if (description) formData.append('description', description);
    return (await api.post('/announcements/audio/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })).data;
  },

  deleteAudio: async (id: number): Promise<any> =>
    (await api.delete(`/announcements/audio/${id}`)).data,

  getTtsLanguages: async (): Promise<any> =>
    (await api.get('/announcements/tts/languages')).data,

  synthesizeTtsPreview: async (text: string, language = 'en', voice = 'female_emergency'): Promise<any> =>
    (await api.post('/announcements/tts/synthesize', null, { params: { text, language, voice } })).data,

  getQueue: async (): Promise<any[]> =>
    (await api.get('/announcements/queue')).data,

  cancelQueued: async (id: number): Promise<any> =>
    (await api.delete(`/announcements/queue/${id}`)).data,

  getHistory: async (params?: any): Promise<any> =>
    (await api.get('/announcements/history', { params })).data,

  triggerManual: async (data: any): Promise<any> =>
    (await api.post('/announcements/trigger-manual', data)).data,

  refineWithNvidia: async (data: { event_type: string; context?: any; tone?: string; language?: string }): Promise<any> =>
    (await api.post('/announcements/nvidia-refine', data)).data,

  getSettings: async (): Promise<any> =>
    (await api.get('/announcements/settings')).data,

  updateSettings: async (data: any): Promise<any> =>
    (await api.put('/announcements/settings', data)).data,

  triggerTimetableBell: async (data: { bell_type: string; person_name?: string; time_str?: string }): Promise<any> =>
    (await api.post('/announcements/timetable/trigger', data)).data,

  runLunchAudit: async (): Promise<any> =>
    (await api.post('/announcements/lunch-check')).data,
};